import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState, Session } from "ezStore";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Button, Checkbox, FileUpload, FormGroup, Select, showSnackbar, Textarea } from "@cofano/cck";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { Alert, Drawer, Tooltip } from "@mui/material";
import { DataFormat, hasPluginPermission, usePermission } from "utils";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import DatePicker from "components/cck/DatePicker";
import FormField from "components/cck/FormField";
import { CustomDialog } from "components/dialogs/Dialogs";
import StatusChangerSailingReportField from "./StatusChangerSailingReportField";
import StatusChangerLoadingLocationField from "./StatusChangerLoadingLocationField";
import StatusChangerMeasurementField from "./StatusChangerMeasurementField";
import HistoryStatusChanges from "./HistoryStatusChanges";
import {
	buildDocumentDescription,
	cargoActionTypeToDocumentType,
	statusTypeToDocumentEntity,
	uploadDocuments,
} from "components/documents/DocumentsUtils";
import { documentsByEntityQuery, Entity } from "components/documents/Documents";
import {
	buildStatusOptions,
	getActualStatusLabel,
	getEstimatedStatusLabel,
	isActualTimeRequired,
	isEstimateTimeRequired,
	shouldShowEstimateTime,
} from "view/statuses/utils/StatusUtils";

import { Relation, StatusCallEntity, StatusCargoEntity, StatusDeclarationEntity, StatusInvoiceEntity, StatusVoyageEntity } from "types/index";
import { BatchStatusChangeResponse } from "__generated__/graphql";
import { CallType, RelationType, StatusEstimateType, StatusType } from "__generated__/enums";
import { StatusFragmentFragment as StatusFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import {
	BatchStatusChangeDocument,
	BatchStatusChangeMutation,
	InsertStatusChangeDocument,
	InsertStatusChangeMutation,
} from "__generated__/view/statuses/graphql/mutations.generated";

import "../../styling/styling.scss";
import { DateUtils } from "utils/DateUtils";
import { PdfButton } from "view/charters/dossier/ship/ShipDetailsBlock";
import { CharterPdfType } from "__generated__/enums";
import StatusChip from "view/statuses/components/changer/StatusChip";
import StatusChangerRequiredTanksField from "view/statuses/components/changer/StatusChangerRequiredTanksField";
import { GetMaxFileSizeDocument } from "__generated__/view/admin/graphql/admin.queries.generated";

type CallTimes = {
	eta?: number | null;
	etso?: number | null;
	eteo?: number | null;
	etd?: number | null;
};

const StatusChanger = ({
	session: propSession,
	statusType,
	entity,
	siblingEntities,
	refetch,
	showHistory,
	onStatusChange,
	callTimes,
	dialogProps,
}: {
	session?: Session;
	statusType: StatusType;
	entity: StatusVoyageEntity | StatusCallEntity | StatusCargoEntity | StatusDeclarationEntity | StatusInvoiceEntity;
	siblingEntities?: StatusCargoEntity[];
	refetch: () => void;
	showHistory?: boolean;
	onStatusChange?: (newStatus: StatusFragment) => void;
	callTimes?: CallTimes
	dialogProps?: {
		openDialog: (open: boolean) => void;
	}
}) => {

	const session = propSession || useStoreState(store => store.session);

	const [ dialogOpen, openDialog ] = useState(false);
	const [ drawerOpen, openDrawer ] = useState(false);

	const actionTypes = statusType === StatusType.Cargo
		? (entity as StatusCargoEntity).actions?.map(action => action.type)
		: [];

	let possibleStatuses = buildStatusOptions(session, statusType, entity.lastStatusChange);
	if (actionTypes) {
		possibleStatuses = possibleStatuses
			.filter(s => s.cargoActionType == null || actionTypes.includes(s.cargoActionType));
	}

	const canEdit = possibleStatuses.length > 0 && !entity.lastStatusChange?.status?.closedStatus;

	const status = entity.lastStatusChange?.status;

	return (
		<div onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>

			<StatusChip
				label={status ? DataFormat.status(status, session.locale) : intl.formatMessage({ id: "NO_STATUS_FOUND" })}
				color={status?.color}
				onClick={() => {
					openDialog(true);
					dialogProps?.openDialog(true);
				}}
				disabled={!canEdit}
				onHistoryClick={showHistory ? () => openDrawer(true) : undefined}
			/>

			{dialogOpen && (
				<StatusChangerDialog
					onStatusChange={onStatusChange}
					open={true}
					session={session}
					statusType={statusType}
					entities={[ entity ]}
					siblingEntities={siblingEntities}
					possibleStatuses={possibleStatuses}
					onClose={() => {
						openDialog(false);
						dialogProps?.openDialog(false);
					}}
					refetch={refetch}
					callTimes={callTimes}
				/>
			)}

			{showHistory && (
				<Drawer
					anchor={"right"}
					open={drawerOpen}
					onClose={() => openDrawer(false)}
				>
					<HistoryStatusChanges entity={entity} refetch={refetch} />
				</Drawer>
			)}
		</div>
	);
};

type StatusChangerDialogProps = {
	open: boolean;
	session: Session;
	statusType: StatusType;
	entities: StatusVoyageEntity[] | StatusCallEntity[] | StatusCargoEntity[] | StatusDeclarationEntity[] | StatusInvoiceEntity[];
	siblingEntities?: StatusCargoEntity[];
	possibleStatuses: StatusFragment[];
	onClose: () => void;
	refetch: () => void;
	doBatchRequest?: (
		body: StatusChangerForm,
		setLoading: (loading: boolean) => void,
		onStatusesChanged: (response: BatchStatusChangeResponse) => void
	) => void;
	onStatusChange?: (newStatus: StatusFragment) => void;
	callTimes?: CallTimes;
};

type DocumentTarget = Omit<Entity, "name">;

export type StatusChangerForm = {
	status?: StatusFragment;
	supplier?: Relation;
	actualTime?: number;
	estimateTime?: number;
	comment?: string;
	skipperComment?: string;
	includePricing?: boolean;
	documents?: File[];
};

export const StatusChangerDialog = ({
	open,
	session,
	statusType,
	entities,
	siblingEntities,
	possibleStatuses,
	onClose,
	refetch,
	doBatchRequest,
	onStatusChange,
	callTimes,
}: StatusChangerDialogProps) => {

	const [ loading, setLoading ] = useState(false);
	const [ validSailingReport, setValidSailingReport ] = useState(false);
	const [ validLoadingLocation, setValidLoadingLocation ] = useState(false);
	const [ validMeasurementsChecks, setValidMeasurementChecks ] = useState(true);
	const [ validTanks, setValidTanks ] = useState(true);

	const isSaveButtonDisabled = () => (
		(values.status?.skipperRequiredFillInSailingReport && !validSailingReport) ||
		(values.status?.shouldLoadOnTerminal && !validLoadingLocation) ||
		(values.status?.skipperRequiredFillInWeightVolume && !validMeasurementsChecks) ||
		(values.status?.requireTanks && !validTanks)
	);

	const getCall = () => {
		if (statusType === StatusType.Call && entities.length === 1) {
			return entities[0] as StatusCallEntity;
		}

		return undefined;
	};

	const getCargo = () => {
		if (statusType === StatusType.Cargo && entities.length === 1) {
			return entities[0] as StatusCargoEntity;
		}

		return undefined;
	};

	const getVoyage = () => {
		if (statusType === StatusType.Voyage && entities.length === 1) {
			return entities[0] as StatusVoyageEntity;
		}

		return undefined;
	};

	const getDeclaration = () => {
		if (statusType === StatusType.Declaration && entities.length === 1) {
			return entities[0] as StatusDeclarationEntity;
		}

		return undefined;
	};

	const getInvoice = () => {
		if (statusType === StatusType.Invoice && entities.length === 1) {
			return entities[0] as StatusInvoiceEntity;
		}

		return undefined;
	};

	const getCargoActionCallTimes = (cargoActionType: CallType | null | undefined): CallTimes | undefined => {
		if (!cargoActionType) {
			return undefined;
		}

		return getCargo()?.actions?.find(action => action.type === cargoActionType)?.call ?? undefined;
	};

	const getDefaultEstimateTime = (status: StatusFragment | undefined) => {
		const call = getCall();
		const times = getCargoActionCallTimes(status?.cargoActionType) ?? callTimes;

		switch (status?.estimateType) {
			case StatusEstimateType.Eta: {
				return call?.eta ?? times?.eta ?? undefined;
			}
			case StatusEstimateType.Etso: {
				return call?.etso ?? times?.etso ?? undefined;
			}
			case StatusEstimateType.Eteo: {
				return call?.eteo ?? times?.eteo ?? undefined;
			}
			case StatusEstimateType.Etd: {
				return call?.etd ?? times?.etd ?? undefined;
			}
		}

		return undefined;
	};

	const { isSkipperOrShipManager } = usePermission();
	const { instance } = useCoastguard();
	const client = useApolloClient();
	const { data: { maxFileSize } = { maxFileSize: 0 } } = useQuery(GetMaxFileSizeDocument);
	const canUploadDocuments = statusTypeToDocumentEntity(statusType) !== undefined;
	const externalOperators = hasPluginPermission(session, "externalOperators");
	const hasSiblings = (siblingEntities?.length ?? 0) > 0;
	const { control, handleSubmit, reset, formState, watch, setValue } = useForm<StatusChangerForm>({
		defaultValues: {
			status: undefined,
			supplier: undefined,
			estimateTime: undefined,
			actualTime: DateUtils.getTimestamp(new Date()) ?? undefined,
			comment: undefined,
			skipperComment: undefined,
			includePricing: true,
			documents: [],
		},
	});

	const values = watch();
	const showActualTime = values.status && isActualTimeRequired(values.status.requireActualTime, session);
	const showEstimateTime = values.status && shouldShowEstimateTime(values.status.estimateType);

	const [ changeStatus, { loading: singleLoading } ] = useMutation<InsertStatusChangeMutation>(InsertStatusChangeDocument);
	const [ batchStatusChange, { loading: bulkLoading } ] = useMutation<BatchStatusChangeMutation>(BatchStatusChangeDocument);

	const buildStatusChangeInput = (entityId: number, data: StatusChangerForm) => ({
		status: {
			id: data.status?.id || 0,
		},
		actualTime: showActualTime ? data.actualTime : undefined,
		estimateTime: showEstimateTime ? data.estimateTime : undefined,
		comment: data.comment,
		skipperComment: data.skipperComment,
		supplier: data.supplier ? {
			id: data.supplier.id,
		} : undefined,
		entity: {
			id: entityId,
			type: statusType,
		},
		includePricing: data.includePricing || false,
	});

	const buildDocumentTarget = (status: StatusFragment, entity: { id: number }): DocumentTarget | undefined => {
		const documentEntity = statusTypeToDocumentEntity(statusType);

		if (!documentEntity) {
			return undefined;
		}

		if (statusType === StatusType.Cargo && status.cargoActionType) {
			const action = (entity as StatusCargoEntity).actions
				?.find(a => a.type === status.cargoActionType && a.id != null);

			if (!action) {
				return undefined;
			}

			return {
				id: entity.id,
				type: cargoActionTypeToDocumentType(status.cargoActionType),
				cargoActionId: action.id,
				callId: action.call?.id,
			};
		}

		return { id: entity.id, type: documentEntity };
	};

	const selectedStatus = values.status;
	const existingDocumentTargets = selectedStatus?.requireDocuments
		? entities
			.map(entity => buildDocumentTarget(selectedStatus, entity))
			.filter((target): target is DocumentTarget => target !== undefined)
		: [];

	const { data: existingDocuments } = useQuery(documentsByEntityQuery, {
		variables: {
			entities: existingDocumentTargets.map(target => ({
				entityId: target.cargoActionId ?? target.id,
				entityType: target.type,
			})),
		},
		skip: existingDocumentTargets.length === 0,
	});

	const hasExistingDocuments = (existingDocuments?.documentsByEntity.length ?? 0) > 0;

	const uploadStatusDocuments = async (data: StatusChangerForm, targets: { id: number }[]) => {
		const status = data.status;

		if (!status?.requireDocuments || !canUploadDocuments || !data.documents?.length) {
			return true;
		}

		const documents = data.documents;
		const documentTargets = targets.map(target => buildDocumentTarget(status, target));

		if (documentTargets.some(target => target === undefined)) {
			showSnackbar(intl.formatMessage({ id: "STATUS_CHANGE_DOCUMENT_ACTION_NOT_FOUND" }), { variant: "error" });
			return false;
		}

		let failed;

		try {
			({ failed } = await uploadDocuments(
				instance,
				documents,
				(documentTargets as DocumentTarget[]).map(target => ({ ...target, name: getDialogTitle() ?? "" })),
				{ description: buildDocumentDescription(documents) },
			));
		} catch {
			showSnackbar(intl.formatMessage({ id: "UPLOAD_FAILED" }), { variant: "error" });
			return false;
		}

		if (failed.length > 0) {
			return false;
		}

		await client.refetchQueries({ include: [ documentsByEntityQuery ] })
			.catch(() => undefined);

		return true;
	};

	const save = async (data: StatusChangerForm) => {
		setLoading(true);

		if (!await uploadStatusDocuments(data, doBatchRequest ? entities : [ entities[0] ])) {
			setLoading(false);
			return;
		}

		if (doBatchRequest) {
			doBatchRequest(data, setLoading, onBatchStatusChanged);
		} else {
			changeStatus({
				variables: {
					input: buildStatusChangeInput(entities[0].id, data),
				},
			}).then(() => {
				onStatusChanged();

				if (onStatusChange && data.status) {
					onStatusChange(data.status);
				}
			}).finally(() => {
				setLoading(false);
			});
		}
	};

	const saveBulk = async (data: StatusChangerForm) => {
		const siblings = siblingEntities ?? [];

		if (!doBatchRequest && siblings.length === 0) {
			return;
		}

		const allEntities = doBatchRequest ? entities : [ entities[0], ...siblings ];

		setLoading(true);

		if (!await uploadStatusDocuments(data, allEntities)) {
			setLoading(false);
			return;
		}

		if (doBatchRequest) {
			doBatchRequest(data, setLoading, onBatchStatusChanged);
		} else {
			batchStatusChange({
				variables: {
					input: allEntities.map(entity => buildStatusChangeInput(entity.id, data)),
				},
			}).then(response => {
				if (response.data) {
					onBatchStatusChanged(response.data.batchStatusChange);
				}

				if (onStatusChange && data.status) {
					onStatusChange(data.status);
				}
			}).finally(() => {
				setLoading(false);
			});
		}
	};

	const onStatusChanged = () => {
		showSnackbar(intl.formatMessage({ id: "STATUS_SUCCESSFULLY_CHANGED" }), { variant: "success" });
		onClose();
		reset();
		refetch();
	};

	const onBatchStatusChanged = (response: BatchStatusChangeResponse) => {
		const numberOfSuccesses = response.successes.length;
		const numberOfFailures = response.failures.length;

		if (numberOfFailures > 0) {
			if (numberOfSuccesses > 0) {
				showSnackbar(
					intl.formatMessage({ id: `STATUS_SUCCESSFULLY_CHANGED_FOR_X_AND_FAILED_FOR_Y_${statusType}S` })
						.replace("_X_", numberOfSuccesses.toString())
						.replace("_Y_", numberOfFailures.toString()),
					{ variant: "warning" });
			} else {
				showSnackbar(
					intl.formatMessage({ id: `STATUS_CHANGE_FAILED_FOR_X_${statusType}${numberOfFailures === 1 ? "" : "S"}` })
						.replace("_X_", numberOfFailures.toString()),
					{ variant: "error" });
			}
		} else {
			showSnackbar(
				intl.formatMessage({ id: `STATUS_SUCCESSFULLY_CHANGED_FOR_X_${statusType}${numberOfSuccesses === 1 ? "" : "S"}` })
					.replace("_X_", numberOfSuccesses.toString()),
				{ variant: "success" });
		}

		onClose();
		reset();
		refetch();
	};

	const getDialogTitle = () => {
		const cargo = getCargo();
		const call = getCall();
		const voyage = getVoyage();
		const declaration = getDeclaration();
		const invoice = getInvoice();

		if (voyage) {
			return voyage.voyageNumber;
		} else if (statusType === StatusType.Voyage && entities.length > 1) {
			return intl.formatMessage({ id: "CHANGE_STATUS_FOR_X_VOYAGES" }).replace("_X_", `${entities.length}`);
		}  else if (cargo) {
			return `${intl.formatMessage({ id: "CARGO" })} ${cargo.cargoNumber ?? ""} - ${cargo.product?.name || cargo.containerNumber}`;
		} else if (statusType === StatusType.Cargo && entities.length > 1) {
			return intl.formatMessage({ id: "CHANGE_STATUS_FOR_X_CARGOS" }).replace("_X_", `${entities.length}`);
		} else if (call) {
			return call.location?.description;
		} else if (invoice) {
			return invoice.invoiceNumber || intl.formatMessage({ id: "INVOICE" });
		} else if (declaration) {
			return declaration.externalId || intl.formatMessage({ id: "DECLARATION" });
		} else if (statusType === StatusType.Declaration && entities.length > 1) {
			return intl.formatMessage({ id: "CHANGE_STATUS_FOR_X_DECLARATIONS" }).replace("_X_", `${entities.length}`);
		} else {
			return intl.formatMessage({ id: "CHANGE_STATUS" });
		}
	};

	const isThirdPartyVoyage = externalOperators && statusType == StatusType.Voyage && !(entities[0] as StatusVoyageEntity).ship.operator?.isApplicationRelation;
	const salesCharterId = (entities[0] as StatusVoyageEntity).cargoCharter?.id ?? (entities[0] as StatusVoyageEntity).salesCharterId;
	const salesCharter = salesCharterId ? { id: salesCharterId } : undefined;
	const willNotifySkipper = values.status?.notifySkippers && !isThirdPartyVoyage;
	const willNotifyShipManager = values.status?.notifyShipManagers && !isThirdPartyVoyage;
	const willNotifyShipOwner = values.status?.notifyShipOwners && !isThirdPartyVoyage;
	const willNotifyOperator = values.status?.notifyOperators && isThirdPartyVoyage;
	const someoneIsNotified = willNotifySkipper || willNotifyShipManager || willNotifyShipOwner || willNotifyOperator;

	return (
		<CustomDialog
			open={open}
			title={getDialogTitle()}
			onClose={() => onClose()}
			maxWidth="xs"
			actions={(
				<>
					{hasSiblings && (
						<Tooltip
							placement="top"
							title={<>
								{intl.formatMessage({ id: "APPLY_TO_ALL_TOOLTIP" })}
								<ul style={{ paddingInlineStart: 20, marginBottom: 0 }}>
									{siblingEntities!
										.map(c => (
											<li key={c.id}>
												{c.cargoNumber ? <>{intl.formatMessage({ id: "CARGO" })} {c.cargoNumber} ({c.product?.name ?? ""})</> : c.product?.name}
											</li>
										))}
								</ul>
							</>}
						>
							<span>
								<Button
									onClick={e => {
										e.stopPropagation();
										//don't use type submit because at some places the StatusChanger is used inside a form
										handleSubmit(saveBulk)();
									}}
									variant="outlined"
									disabled={isSaveButtonDisabled() || loading || singleLoading || bulkLoading}
									loading={loading || singleLoading || bulkLoading}
									size="small">
									{intl.formatMessage({ id: "APPLY_TO_ALL" })}
								</Button>
							</span>
						</Tooltip>
					)}

					<Button
						onClick={e => {
							e.stopPropagation();
							//don't use type submit because at some places the StatusChanger is used inside a form
							handleSubmit(save)();
						}}
						disabled={isSaveButtonDisabled() || loading || singleLoading || bulkLoading}
						loading={loading || singleLoading || bulkLoading}
						size="small">
						{intl.formatMessage({ id: "CHANGE_STATUS" })}
					</Button>
				</>
			)}>
			<div className="status-changer-form">
				<form onSubmit={handleSubmit(form => save(form))} style={{ marginBottom: 8 }}>
					<FormGroup label={intl.formatMessage({ id: "DASHBOARD_STATUS" })} overflow="hidden">
						{DataFormat.status(entities[0].lastStatusChange?.status ?? undefined, session.user?.language ?? undefined)}
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "NEW_STATUS" })} overflow="hidden" required>
						<FormField errors={formState.errors} name="status">
							<Controller
								name={"status"}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<Select
										{...field}
										fetchOptions={possibleStatuses}
										popoverFullWidth={true}
										getOptionColor={status => status.color}
										getOptionLabel={status => DataFormat.status(status, session.user?.language ?? undefined)}
										disableSearch={true}
										onChange={(e) => {
											field.onChange(e);
											setValue("estimateTime", getDefaultEstimateTime(e ?? undefined));
										}}
									/>
								)} />
						</FormField>
					</FormGroup>

					{values.status?.supplierRequired && entities.find(entity => !((entity as StatusVoyageEntity).allSuppliersConfigured)) && (
						<FormGroup label={intl.formatMessage({ id: "SUPPLIER" })} overflow="hidden" required>
							<FormField errors={formState.errors} name="supplier">
								<Controller
									name={"supplier"}
									control={control}
									rules={{ required: true }}
									render={({ field }) => <RelationSelect {...field} types={[ RelationType.Supplier ]} />} />
							</FormField>
						</FormGroup>
					)}

					{values.status && showActualTime && (
						<FormGroup label={getActualStatusLabel(values.status.requireActualTime)} overflow="hidden" required={isActualTimeRequired(values.status.requireActualTime, session)}>
							<FormField errors={formState.errors} name="actualTime">
								<Controller
									name={"actualTime"}
									control={control}
									rules={{ required: isActualTimeRequired(values.status.requireActualTime, session) }}
									render={({ field }) =>
										<DatePicker
											{...field}
											showTimeInput
										/>}
								/>
							</FormField>
						</FormGroup>
					)}

					{values.status && shouldShowEstimateTime(values.status.estimateType) && (
						<FormGroup label={getEstimatedStatusLabel(values.status.estimateType)} overflow="hidden" required={isEstimateTimeRequired(values.status.estimateRequirement)}>
							<FormField errors={formState.errors} name="estimateTime">
								<Controller
									name={"estimateTime"}
									control={control}
									rules={{ required: isEstimateTimeRequired(values.status.estimateRequirement) }}
									render={({ field }) => <DatePicker {...field} showTimeInput />} />
							</FormField>
						</FormGroup>
					)}

					{!isSkipperOrShipManager && values.status?.allowComment && (
						<FormGroup label={intl.formatMessage({ id: "COMMENT" })} overflow="hidden">
							<Controller
								name={"comment"}
								control={control}
								render={({ field }) => <Textarea {...field} />} />
						</FormGroup>
					)}

					{values.status?.allowSkipperComment && isSkipperOrShipManager && (
						<FormGroup label={intl.formatMessage({ id: "SKIPPER_COMMENT" })} overflow="hidden">
							<Controller
								name={"skipperComment"}
								control={control}
								render={({ field }) => <Textarea {...field} />} />
						</FormGroup>
					)}

					{values.status?.requireDocuments && canUploadDocuments && (
						<FormGroup
							label={values.status.cargoActionType
								? `${intl.formatMessage({ id: values.status.cargoActionType })} ${intl.formatMessage({ id: "DOCUMENTS" })}`
								: intl.formatMessage({ id: "STATUS_CHANGE_DOCUMENTS" })}
							overflow="hidden"
							required={!hasExistingDocuments}
						>
							<FormField
								name="documents"
								errors={formState.errors}
								validation={intl.formatMessage({ id: "REQUIRED" })}>
								<Controller
									name="documents"
									control={control}
									rules={{ validate: documents => hasExistingDocuments || (documents?.length ?? 0) > 0 }}
									render={({ field }) => (
										<FileUpload
											value={field.value}
											maxSize={maxFileSize}
											onChange={field.onChange}
											onRejected={rejectedFiles => {
												rejectedFiles.forEach(item => {
													showSnackbar((
														<div>
															<p style={{ margin: 0, fontWeight: 600 }}>{item.file.name}</p>
															<p style={{ margin: 0 }}>{`${intl.formatMessage({ id: "FILE_TO_LARGE" }).replace("%d", (maxFileSize / (1024 * 1024)).toString())}. ${intl.formatMessage({ id: "CONTACT_ADMIN" })}`}</p>
														</div>
													), { variant: "error" });
												});
											}} />
									)} />
							</FormField>
						</FormGroup>
					)}
				</form>

				{values.status?.supplierRequired && entities.every(entity => ((entity as StatusVoyageEntity).allSuppliersConfigured)) && (
					<Alert severity="success">
						{intl.formatMessage({ id: "SUPPLIER" })}
					</Alert>
				)}

				{statusType === StatusType.Voyage && (
					<React.Fragment>
						<StatusChangerSailingReportField
							status={values.status}
							entities={entities as StatusVoyageEntity[]}
							onChange={(valid) => setValidSailingReport(valid)} />

						<StatusChangerLoadingLocationField
							status={values.status}
							entities={entities as StatusVoyageEntity[]}
							onChange={(valid) => setValidLoadingLocation(valid)} />
					</React.Fragment>
				)}

				{entities.length === 1 && values.status?.skipperRequiredFillInWeightVolume && (
					<StatusChangerMeasurementField
						voyageId={statusType === StatusType.Voyage ? entities[0].id : undefined}
						callId={statusType === StatusType.Call ? entities[0].id : undefined}
						cargoId={statusType === StatusType.Cargo ? entities[0].id : undefined}
						cargoActionType={statusType === StatusType.Cargo ? values.status.cargoActionType : undefined}
						onChange={(valid) => setValidMeasurementChecks(valid)} />
				)}

				{statusType === StatusType.Cargo && entities.length === 1 && values.status?.requireTanks && (
					<StatusChangerRequiredTanksField
						cargoId={entities[0].id}
						onChange={(valid) => setValidTanks(valid)} />
				)}

				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					{willNotifySkipper && (
						<Alert
							severity="warning"
							action={
								externalOperators && statusType === StatusType.Voyage && salesCharter ? (
									<PdfButton
										charter={salesCharter}
										type={CharterPdfType.NominationToSkipper}
									/>) : undefined}
						>
							{intl.formatMessage({ id: "STATUS_CHANGE_WILL_NOTIFY_SKIPPER" })}
						</Alert>
					)}

					{willNotifyShipManager && (
						<Alert
							severity="warning"
							action={
								externalOperators && statusType === StatusType.Voyage && salesCharter ? (
									<PdfButton
										charter={salesCharter}
										type={CharterPdfType.NominationToShipOwner}
										includePricing={values.includePricing}
									/>) : undefined}
						>
							{intl.formatMessage({ id: "STATUS_CHANGE_WILL_NOTIFY_SHIP_MANAGER" })}
						</Alert>
					)}

					{willNotifyShipOwner && (
						<Alert
							severity="warning"
							action={
								externalOperators && statusType === StatusType.Voyage && salesCharter ? (
									<PdfButton
										charter={salesCharter}
										type={CharterPdfType.NominationToShipOwner}
										includePricing={values.includePricing}
									/>) : undefined}
						>
							{intl.formatMessage({ id: "STATUS_CHANGE_WILL_NOTIFY_SHIP_OWNER" })}
						</Alert>
					)}

					{willNotifyOperator && (
						<Alert
							severity="warning"
							action={
								externalOperators && statusType === StatusType.Voyage && salesCharter ? (
									<PdfButton
										charter={salesCharter}
										type={CharterPdfType.NominationToShipOwner}
										includePricing={values.includePricing}
									/>) : undefined}
						>
							{intl.formatMessage({ id: "STATUS_CHANGE_WILL_NOTIFY_OPERATOR" })}
						</Alert>
					)}

					{externalOperators && someoneIsNotified && (
						<Controller
							name="includePricing"
							control={control}
							render={({ field }) =>
								<Checkbox
									label={intl.formatMessage({ id: "INCLUDE_PRICING_IN_NOMINATION" })}
									{...field}
									checked={field.value}
									value={undefined}
								/>
							}
						/>
					)}
				</div>
			</div>
		</CustomDialog>
	);
};

export default StatusChanger;
